import { API_ROUTES } from "@/routes";
import apiClient from "./client";

export interface FeatureFlagsResponse {
  flags: Record<string, boolean>;
}

export interface GlobalSettingsResponse {
  settings: Record<string, unknown>;
}

export interface ManagementUser {
  user_id: number;
  uuid: string;
  user_email: string;
  user_name: string;
  user_active: boolean;
  events_count: number;
  events_count_free: number;
  events_count_standard: number;
  events_count_premium: number;
  credits_standard: number;
  credits_premium: number;
  stripe_connected: boolean;
  created_at?: string;
}

export interface UserProfileEvent {
  event_id: number;
  event_uuid: string;
  event_name: string;
  event_url: string;
  event_date: string | null;
  event_created_date: string | null;
  event_active: boolean;
  plan: string;
  /** JUB-157: the event is in the Trash (still listed for support). */
  trashed?: boolean;
  /** JUB-157: hidden from the customer's dashboard list. */
  archived?: boolean;
  guests_count: number;
  confirmed_guests_count: number;
  gifts_total: number;
  gifts_active: number;
  gifts_inactive: number;
}

export interface UserProfileResponse {
  user: {
    user_id: number;
    uuid: string;
    user_email: string;
    user_name: string;
    user_active: boolean;
    stripe_connected: boolean;
    stripe_payouts_enabled: boolean;
  };
  events: UserProfileEvent[];
}

export async function getFeatureFlags(): Promise<FeatureFlagsResponse> {
  const { data } = await apiClient.get<FeatureFlagsResponse>(API_ROUTES.management.featureFlags());
  return data;
}

export async function updateFeatureFlag(key: string, enabled: boolean): Promise<FeatureFlagsResponse> {
  const { data } = await apiClient.patch<FeatureFlagsResponse>(API_ROUTES.management.featureFlagToggle(key), {
    enabled,
  });
  return data;
}

export async function getGlobalSettings(): Promise<GlobalSettingsResponse> {
  const { data } = await apiClient.get<GlobalSettingsResponse>(API_ROUTES.management.settings());
  return data;
}

export async function getManagementUsers(options?: {
  cursor?: string;
  sort?: string;
  direction?: "asc" | "desc";
  /** Case-insensitive match on user name OR email (JUB-92). */
  search?: string;
}): Promise<PaginatedResponse<ManagementUser>> {
  const params: Record<string, string | number> = { per_page: 50 };
  if (options?.cursor) params.cursor = options.cursor;
  if (options?.search?.trim()) params.search = options.search.trim();
  if (options?.sort) params.sort = options.sort;
  if (options?.direction) params.direction = options.direction;
  const { data } = await apiClient.get<PaginatedResponse<ManagementUser>>(API_ROUTES.management.users(params));
  return data;
}

export async function getUserProfile(userId: number): Promise<UserProfileResponse> {
  const { data } = await apiClient.get<UserProfileResponse>(API_ROUTES.management.userProfile(userId));
  return data;
}

/**
 * Account-level plan keys PlanManagementController@updatePlan accepts
 * (server/app/Http/Controllers/Management/PlanManagementController.php:80).
 * This is NOT the same value space as an event's `event_plan` — see
 * AccountPlanResponse doc comment below.
 */
export type AccountPlanKey = "free" | "standard" | "premium";

export interface AccountPlanPayment {
  paymentId: number;
  plan: string;
  processor: string;
  createdAt: string | null;
  expiresAt?: string | null;
}

/**
 * Response of GET /management/accounts/{id}/plan (JUB-42).
 *
 * IMPORTANT: this is the legacy ACCOUNT-level plan (derived from the
 * account's Payment history), not the per-event plan shown in the events
 * table on this page (`UserProfileEvent.plan`, backed by `Event.event_plan`
 * since the 2026-01-26 event-level-plans migration). Changing this value
 * does not change any event's plan or grant plan credits — see the
 * management.planOverride.warning i18n string surfaced in the UI.
 */
export interface AccountPlanResponse {
  userId: number;
  currentPlan: AccountPlanKey | string;
  latestPayment: AccountPlanPayment | null;
}

export interface UpdateAccountPlanResponse {
  userId: number;
  plan: AccountPlanKey;
  expiresAt?: string | null;
  paymentId?: number;
  message: string;
}

/**
 * @deprecated JUB-157: plans belong to events; the account-plan endpoints are
 * kept for reporting compatibility only and no UI offers them any more.
 */
export async function getAccountPlan(userId: number): Promise<AccountPlanResponse> {
  const { data } = await apiClient.get<{ data: AccountPlanResponse }>(API_ROUTES.management.getPlan(userId));
  return data.data;
}

/** @deprecated JUB-157: see getAccountPlan. Grants nothing to any event. */
export async function updateAccountPlan(
  userId: number,
  plan: AccountPlanKey
): Promise<UpdateAccountPlanResponse> {
  const { data } = await apiClient.patch<{ data: UpdateAccountPlanResponse }>(
    API_ROUTES.management.updatePlan(userId),
    { plan }
  );
  return data.data;
}

/**
 * Event-level plan keys PlanManagementController@updateEventPlan validates
 * against (server/app/Http/Controllers/Management/PlanManagementController.php).
 * Same literal values as AccountPlanKey, but kept as a distinct type on
 * purpose — this is the mechanism that actually gates a customer's event
 * (JUB-138/JUB-142), and the two are deliberately not interchangeable (see
 * the controller's class docblock).
 */
export type EventPlanKey = "free" | "standard" | "premium";

/** Response of GET /management/events/{uuid}/plan (JUB-138/JUB-142). */
export interface EventPlanResponse {
  eventUuid: string;
  plan: EventPlanKey | string;
  hasGiftsDirect: boolean;
  guestLimit: number | null;
}

export interface UpdateEventPlanResponse {
  eventUuid: string;
  plan: EventPlanKey;
  previousPlan: EventPlanKey | string;
  message: string;
}

/** Gets an event's current plan and a couple of derived gates (superadmin only). */
export async function getEventPlan(eventUuid: string): Promise<EventPlanResponse> {
  const { data } = await apiClient.get<{ data: EventPlanResponse }>(API_ROUTES.management.getEventPlan(eventUuid));
  return data.data;
}

/**
 * Grants or revokes a plan on a specific event (superadmin only). This is
 * the endpoint that actually changes what the customer's event can do —
 * see PlanManagementController's class docblock.
 */
export async function updateEventPlan(eventUuid: string, plan: EventPlanKey): Promise<UpdateEventPlanResponse> {
  const { data } = await apiClient.patch<{ data: UpdateEventPlanResponse }>(
    API_ROUTES.management.updateEventPlan(eventUuid),
    { plan }
  );
  return data.data;
}

/**
 * JUB-43 — admin revenue/payment overview. See RevenueController's docblock
 * (server side) for the exact definition behind every figure: collected
 * revenue is what was actually charged (post-voucher-discount), $0 and
 * failed payments never count, and "MRR" is intentionally not returned as a
 * number — Jubiel sells one-time per-event plans, not subscriptions.
 */
export interface RevenuePlanDistribution {
  free: number;
  standard: number;
  premium: number;
}

export interface RevenueMonthlyBucket {
  month: string; // "YYYY-MM"
  collected: number;
  paid_count: number;
}

export interface RevenueSummary {
  total_collected_all_time: number;
  collected_last_30_days: number;
  collected_current_calendar_month: number;
  new_paid_events_last_30_days: number;
  monthly_breakdown: RevenueMonthlyBucket[];
  excluded_non_ron_successful_count: number;
}

export interface RecentPayment {
  payment_id: number;
  user_id: number;
  user_uuid: string | null;
  user_name: string | null;
  user_email: string | null;
  event_id: number | null;
  event_uuid: string | null;
  event_name: string | null;
  plan: string | null;
  amount: number;
  currency: string;
  voucher_applied: boolean;
  date: string | null;
}

export interface RevenueResponse {
  currency: string;
  generated_at: string;
  plan_distribution: RevenuePlanDistribution;
  paying_users_count: number;
  revenue: RevenueSummary;
  mrr_not_applicable: boolean;
  mrr_note: string;
  recent_payments: RecentPayment[];
}

export async function getRevenue(): Promise<RevenueResponse> {
  const { data } = await apiClient.get<RevenueResponse>(API_ROUTES.management.revenue());
  return data;
}

export interface UpdateUserStatusResponse {
  message_key: string;
  data: {
    user_id: number;
    active: boolean;
  };
}

/**
 * Activate or deactivate a user account (JUB-41, superadmin only).
 */
export async function updateUserStatus(userId: number, active: boolean): Promise<UpdateUserStatusResponse> {
  const { data } = await apiClient.patch<UpdateUserStatusResponse>(API_ROUTES.management.updateUserStatus(userId), {
    active,
  });
  return data;
}

export interface SoftDeletedAccount {
  user_id: number;
  user_email: string;
  user_name: string;
  deleted_at: string;
  retention_until: string | null;
}

export interface SoftDeletedEvent {
  event_id: number;
  event_uuid: string;
  event_name: string;
  event_user_id: number;
  deleted_at: string;
  retention_until: string | null;
}

export interface PaginatedResponse<T> {
  items: T[];
  cursor: string | null;
  hasMore: boolean;
}

/**
 * Get soft-deleted accounts
 */
export async function getDeletedAccounts(
  cursor?: string,
  perPage: number = 20
): Promise<PaginatedResponse<SoftDeletedAccount>> {
  const params: Record<string, string | number> = {
    per_page: perPage,
  };
  if (cursor) {
    params.cursor = cursor;
  }

  const response = await apiClient.get<PaginatedResponse<SoftDeletedAccount>>(
    API_ROUTES.management.deletedAccounts(params)
  );

  return response.data;
}

/**
 * Get soft-deleted events
 */
export async function getDeletedEvents(
  cursor?: string,
  perPage: number = 20
): Promise<PaginatedResponse<SoftDeletedEvent>> {
  const params: Record<string, string | number> = {
    per_page: perPage,
  };
  if (cursor) {
    params.cursor = cursor;
  }

  const response = await apiClient.get<PaginatedResponse<SoftDeletedEvent>>(
    API_ROUTES.management.deletedEvents(params)
  );

  return response.data;
}

/**
 * Restore a soft-deleted account
 */
export async function restoreAccount(accountId: number): Promise<void> {
  await apiClient.post(API_ROUTES.management.restoreAccount(accountId), {});
}

/**
 * Restore a soft-deleted event
 */
export async function restoreEvent(eventUuid: string): Promise<void> {
  await apiClient.post(API_ROUTES.management.restoreEvent(eventUuid), {});
}



/** JUB-157 (F12a): one administrator action recorded on a purchase. */
export interface AdminPurchaseAuditEntry {
  at: string | null;
  actorUserId: number | null;
  action: string;
  reason: string | null;
  relatedPurchaseId: number | null;
}

/** JUB-157: one plan purchase, as the administrator sees it. */
export interface AdminPurchase {
  id: number;
  plan: "standard" | "premium";
  status: "active" | "unassigned" | "needs_review" | "consumed" | "refunded" | "transferred" | "revoked" | "superseded";
  eventUuid: string | null;
  eventName: string | null;
  amount: number | null;
  currency: string | null;
  boundAt: string | null;
  createdAt: string | null;
  userId: number | null;
  eventId: number | null;
  paymentId: number | null;
  source: string;
  grandfathered: boolean;
  transferredToPurchaseId: number | null;
  transferredByUserId: number | null;
  transferReason: string | null;
  note: string | null;
  eventTrashed: boolean;
  eventArchived: boolean;
  eventPurged: boolean;
  /** Additive (F12a); older responses omit it. */
  auditTrail?: AdminPurchaseAuditEntry[];
}

type Envelope<T> = { message_key?: string; data?: T };

export async function getUserPurchases(userId: number | string): Promise<AdminPurchase[]> {
  const { data } = await apiClient.get<Envelope<{ items: AdminPurchase[] }>>(`/management/purchases`, {
    params: { userId },
  });
  return data?.data?.items ?? [];
}

/** Superadmin exception: move an unused purchase to another event of the same owner. */
export async function transferPurchase(purchaseId: number, toEventUuid: string, reason: string): Promise<void> {
  await apiClient.post(`/management/purchases/${purchaseId}/transfer`, { toEventUuid, reason });
}

/** Superadmin: apply an unassigned / needs-review purchase to an event. */
export async function assignPurchase(purchaseId: number, eventUuid: string, reason: string): Promise<void> {
  await apiClient.post(`/management/purchases/${purchaseId}/assign`, { eventUuid, reason });
}

/** The machine-readable reason of a refused purchase action (error.errors.reason). */
export function purchaseRefusalReason(error: unknown): string | null {
  const reason = (error as { response?: { data?: { error?: { errors?: { reason?: string } } } } })?.response?.data?.error?.errors?.reason;
  return typeof reason === "string" ? reason : null;
}
